import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Stack,
  Tab,
  Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { LiftsList } from './LiftsList';
import { LiftFormDialog } from './LiftFormDialog';
import { WorkshopEquipmentList } from './WorkshopEquipmentList';
import { WorkshopEquipmentFormDialog } from './WorkshopEquipmentFormDialog';
import { useAuthStore } from '@/stores/authStore';

type EquipmentSubTab = 'lifts' | 'other';

export function EquipmentManagement() {
  const { t } = useTranslation('equipment');
  const [subTab, setSubTab] = useState<EquipmentSubTab>('lifts');
  const [createLiftOpen, setCreateLiftOpen] = useState(false);
  const [createEquipmentOpen, setCreateEquipmentOpen] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Lift and equipment mutations require equipment.manage server-side; hide
  // what would 403.
  const canManageEquipment = hasPermission('equipment.manage');

  const isLifts = subTab === 'lifts';

  return (
    <Box>
      <Stack spacing={3}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('subtitle')}
            </Typography>
          </Box>
          {canManageEquipment && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                if (isLifts) {
                  setCreateLiftOpen(true);
                } else {
                  setCreateEquipmentOpen(true);
                }
              }}
            >
              {isLifts ? t('addLift') : t('addEquipment')}
            </Button>
          )}
        </Box>

        {/* Sub-tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={subTab}
            onChange={(_e, value: EquipmentSubTab) => { setSubTab(value); }}
            variant="scrollable"
            scrollButtons="auto"
            aria-label={t('title')}
          >
            <Tab value="lifts" label={t('lifts.title')} />
            <Tab value="other" label={t('equipmentList.title')} />
          </Tabs>
        </Box>

        {/* Tab content */}
        {isLifts ? <LiftsList /> : <WorkshopEquipmentList />}

        {/* Create Dialogs */}
        <LiftFormDialog
          open={createLiftOpen}
          onClose={() => { setCreateLiftOpen(false); }}
        />
        <WorkshopEquipmentFormDialog
          open={createEquipmentOpen}
          onClose={() => { setCreateEquipmentOpen(false); }}
        />
      </Stack>
    </Box>
  );
}
